import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Copy, Check, Code } from 'lucide-react';
import { EntityAnchor, useEntityAnchors } from '@/components/EntityAnchor';

// Code snippet component
function CodeBlock({ code, language = 'tsx' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-muted/50 rounded-lg p-4 overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

// Demo content for auto-insertion example
const DEMO_PARAGRAPHS = [
  '这是第一段示例内容，展示了 EntityAnchor 组件如何在长内容中自动插入。',
  '第二段内容继续延伸，讨论产品开发和创伤探索的交叉点。',
  '第三段内容会触发锚点插入，因为我们设置了每3段插入一次。',
  '第四段内容继续，保持叙事的连贯性。',
  '第五段内容，读者可以看到核心实体信息的重复强化。',
  '第六段会再次插入锚点，确保AI爬虫获取一致的实体信息。',
  '最后一段内容，总结整个示例的展示效果。',
];

export default function ComponentShowcase() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  // Demo state
  const [demoLang, setDemoLang] = useState<'zh' | 'en'>('zh');
  const [showMetrics, setShowMetrics] = useState(true);
  const [showIdentities, setShowIdentities] = useState(true);
  const [useFull, setUseFull] = useState(false);

  // Auto-insertion demo
  const { shouldInsertAnchor, anchorPositions } = useEntityAnchors(DEMO_PARAGRAPHS.length, {
    interval: 3,
    minItems: 2,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation isHomePage={false} />

      <main className="container mx-auto px-4 py-24 max-w-5xl">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '..' })}
          className="mb-8 -ml-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回
        </Button>

        {/* Header */}
        <header className="mb-12">
          <Badge variant="secondary" className="mb-4">
            <Code className="mr-1 h-3 w-3" />
            组件文档
          </Badge>
          <h1 className="text-4xl font-bold mb-4">EntityAnchor 组件</h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            用于在长内容中插入核心实体信息锚点，提升 AI 爬虫的引用一致性和 SEO 效果。
          </p>
        </header>

        {/* Interactive Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>交互控制</CardTitle>
            <CardDescription>调整参数查看不同效果</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="lang"
                  checked={demoLang === 'en'}
                  onCheckedChange={(checked) => setDemoLang(checked ? 'en' : 'zh')}
                />
                <Label htmlFor="lang">English</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="metrics"
                  checked={showMetrics}
                  onCheckedChange={setShowMetrics}
                />
                <Label htmlFor="metrics">显示指标</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="identities"
                  checked={showIdentities}
                  onCheckedChange={setShowIdentities}
                />
                <Label htmlFor="identities">显示身份</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="full"
                  checked={useFull}
                  onCheckedChange={setUseFull}
                />
                <Label htmlFor="full">完整描述</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variants Showcase */}
        <Tabs defaultValue="inline" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="inline">Inline</TabsTrigger>
            <TabsTrigger value="subtle">Subtle</TabsTrigger>
            <TabsTrigger value="block">Block</TabsTrigger>
            <TabsTrigger value="card">Card</TabsTrigger>
          </TabsList>

          {/* Inline Variant */}
          <TabsContent value="inline" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Inline 变体</CardTitle>
                <CardDescription>
                  内联样式，适合在段落末尾添加简短的实体引用
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-muted-foreground">
                    这是一段示例文本，展示了 borninsea 平台的核心理念
                    <EntityAnchor variant="inline" lang={demoLang} full={useFull} />
                  </p>
                </div>
                <CodeBlock code={`<p>
  这是一段示例文本
  <EntityAnchor variant="inline" lang="${demoLang}" />
</p>`} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subtle Variant */}
          <TabsContent value="subtle" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Subtle 变体</CardTitle>
                <CardDescription>
                  低调的左边框样式，适合在段落之间插入
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-muted-foreground mb-0">这是上一段内容...</p>
                  <EntityAnchor variant="subtle" lang={demoLang} full={useFull} />
                  <p className="text-muted-foreground mt-0">这是下一段内容...</p>
                </div>
                <CodeBlock code={`<EntityAnchor variant="subtle" lang="${demoLang}" />`} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Block Variant */}
          <TabsContent value="block" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Block 变体</CardTitle>
                <CardDescription>
                  区块样式，带背景色，可选显示指标数据
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <EntityAnchor 
                  variant="block" 
                  lang={demoLang} 
                  full={useFull}
                  showMetrics={showMetrics} 
                />
                <CodeBlock code={`<EntityAnchor 
  variant="block" 
  lang="${demoLang}" 
  showMetrics={${showMetrics}}
  full={${useFull}}
/>`} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Card Variant */}
          <TabsContent value="card" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Card 变体</CardTitle>
                <CardDescription>
                  完整卡片样式，包含头像、身份标签和指标
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <EntityAnchor 
                  variant="card" 
                  lang={demoLang} 
                  full={useFull}
                  showMetrics={showMetrics}
                  showIdentities={showIdentities}
                />
                <CodeBlock code={`<EntityAnchor 
  variant="card" 
  lang="${demoLang}" 
  full={${useFull}}
  showMetrics={${showMetrics}}
  showIdentities={${showIdentities}}
/>`} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Auto-Insertion Demo */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-4">自动插入示例</h2>
          <p className="text-muted-foreground mb-6">
            使用 <code className="bg-muted px-1 rounded">useEntityAnchors</code> Hook 
            自动在长内容中计算锚点插入位置（每3段插入一次）
          </p>

          <Card>
            <CardHeader>
              <CardTitle>长内容自动锚点</CardTitle>
              <CardDescription>
                锚点位置: {anchorPositions.join(', ')}（段落索引）
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                {DEMO_PARAGRAPHS.map((para, index) => (
                  <div key={index}>
                    <p className="text-muted-foreground">
                      <span className="text-xs text-muted-foreground/50 mr-2">[{index}]</span>
                      {para}
                    </p>
                    {shouldInsertAnchor(index) && (
                      <EntityAnchor variant="subtle" lang={demoLang} />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mt-6">
            <h3 className="font-semibold mb-2">使用代码</h3>
            <CodeBlock code={`import { EntityAnchor, useEntityAnchors } from '@/components/EntityAnchor';

const { shouldInsertAnchor } = useEntityAnchors(paragraphs.length, {
  interval: 3,    // 每3段插入一次
  minItems: 2,    // 至少2段后才开始插入
  maxAnchors: 5,  // 最多插入5个锚点
});

{paragraphs.map((para, index) => (
  <div key={index}>
    <p>{para}</p>
    {shouldInsertAnchor(index) && (
      <EntityAnchor variant="subtle" lang="zh" />
    )}
  </div>
))}`} />
          </div>
        </section>

        {/* API Reference */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-4">API 参考</h2>
          
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>EntityAnchor Props</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">属性</th>
                      <th className="text-left py-2 font-medium">类型</th>
                      <th className="text-left py-2 font-medium">默认值</th>
                      <th className="text-left py-2 font-medium">说明</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 font-mono">lang</td>
                      <td className="py-2">'zh' | 'en'</td>
                      <td className="py-2">'zh'</td>
                      <td className="py-2">显示语言</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">variant</td>
                      <td className="py-2">'inline' | 'subtle' | 'block' | 'card'</td>
                      <td className="py-2">'inline'</td>
                      <td className="py-2">视觉变体</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">full</td>
                      <td className="py-2">boolean</td>
                      <td className="py-2">false</td>
                      <td className="py-2">使用完整描述</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">showMetrics</td>
                      <td className="py-2">boolean</td>
                      <td className="py-2">false</td>
                      <td className="py-2">显示指标数据</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">showIdentities</td>
                      <td className="py-2">boolean</td>
                      <td className="py-2">false</td>
                      <td className="py-2">显示身份标签</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">className</td>
                      <td className="py-2">string</td>
                      <td className="py-2">-</td>
                      <td className="py-2">自定义样式类</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>useEntityAnchors Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">选项</th>
                      <th className="text-left py-2 font-medium">类型</th>
                      <th className="text-left py-2 font-medium">默认值</th>
                      <th className="text-left py-2 font-medium">说明</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 font-mono">interval</td>
                      <td className="py-2">number</td>
                      <td className="py-2">3</td>
                      <td className="py-2">每N个项目插入一次锚点</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">minItems</td>
                      <td className="py-2">number</td>
                      <td className="py-2">2</td>
                      <td className="py-2">首个锚点前的最小项目数</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">maxAnchors</td>
                      <td className="py-2">number</td>
                      <td className="py-2">5</td>
                      <td className="py-2">最大锚点数量</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Best Practices */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-4">最佳实践</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base text-green-600">✅ 推荐</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• 在长内容（500+字）中使用</p>
                <p>• 每300-500字插入一个锚点</p>
                <p>• 使用 subtle 变体保持阅读流畅</p>
                <p>• 在关键页面使用 block/card 变体</p>
                <p>• 保持中英文一致性</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base text-red-600">❌ 避免</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• 在短内容中过度使用</p>
                <p>• 连续多个锚点堆叠</p>
                <p>• 在表格/列表中间插入</p>
                <p>• 忽略语言一致性</p>
                <p>• 使用过于显眼的 card 变体干扰阅读</p>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
